
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Encounter History Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Extract clinical notes and relevant data from encounter for history tracking
 */
export const extractClinicalData = (encounterData) => {
  const clinicalData = {
    // Clinical Notes - comprehensive extraction
    notes: {
      consultationNotes: encounterData.notes?.consultationNotes || '',
      progressNotes: encounterData.notes?.progressNotes || '',
      nurseNotes: encounterData.notes?.nurseNotes || '',
      procedureNotes: encounterData.notes?.procedureNotes || '',
      phoneNotes: encounterData.notes?.phoneNotes || '',
      simpleNotes: encounterData.notes?.simpleNotes || '',
      // Additional note types that might exist
      dischargeNotes: encounterData.notes?.dischargeNotes || '',
      treatmentPlan: encounterData.notes?.treatmentPlan || '',
      followUpInstructions: encounterData.notes?.followUpInstructions || '',
      soapNotes: {
        subjective: encounterData.notes?.soapNotes?.subjective || '',
        objective: encounterData.notes?.soapNotes?.objective || '',
        assessment: encounterData.notes?.soapNotes?.assessment || '',
        plan: encounterData.notes?.soapNotes?.plan || ''
      },
      // Store any other notes that might be in the object
      ...(encounterData.notes && typeof encounterData.notes === 'object' ? encounterData.notes : {})
    },
    
    // Other Clinical Fields
    chiefComplaint: encounterData.chiefComplaint || '',
    initialAssessment: encounterData.initialAssessment || '',
    actionPlan: encounterData.actionPlan || '',
    
    // Provider Information
    provider: encounterData.provider || null,
    
    // Vitals (if changed)
    vitals: encounterData.vitals || null,
    
    // Symptoms
    symptoms: encounterData.symptoms || [],
    
    // Additional clinical data
    diagnosis: encounterData.diagnosis || null,
    medications: encounterData.medications || [],
    allergies: encounterData.allergies || []
  };
  
  return clinicalData;
};

/**
 * Check if clinical data has meaningful changes (not just empty strings)
 */
export const hasClinicalChanges = (clinicalData) => {
  // Check notes
  const notes = clinicalData.notes || {};
  const hasNotes = Object.values(notes).some(noteValue => {
    if (typeof noteValue === 'string') {
      return noteValue.trim().length > 0;
    }
    if (typeof noteValue === 'object' && noteValue !== null) {
      // For soapNotes object
      return Object.values(noteValue).some(value => typeof value === 'string' && value.trim().length > 0);
    }
    return false;
  });
  
  // Check other clinical fields
  const hasOtherClinical = [
    clinicalData.chiefComplaint,
    clinicalData.initialAssessment,
    clinicalData.actionPlan
  ].some(field => field && field.trim && field.trim().length > 0);
  
  // Check if symptoms array has content
  const hasSymptoms = clinicalData.symptoms && clinicalData.symptoms.length > 0;
  
  return hasNotes || hasOtherClinical || hasSymptoms;
};

/**
 * Save encounter history record using secure backend API
 */
export const saveEncounterHistory = async (encounterId, encounterData, userId, changeType = 'update') => {
  try {
    //const clinicalData = extractClinicalData(encounterData);
    
    console.log(`Saving encounter history for ${encounterId} - Change type: ${changeType}`);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/clinical/encounters/${encounterId}/history`,
      {
        encounterData,
        changeType
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Saved encounter history record ${response.data.id} for encounter ${encounterId} - Version: ${response.data.version}`);
    return response.data.id;
    
  } catch (error) {
    console.error('Error saving encounter history:', error);
    // Don't throw - history saving should not prevent encounter updates
    return null;
  }
};

/**
 * Get encounter history for a specific encounter using secure backend API
 */
export const getEncounterHistory = async (encounterId, limitCount = 50) => {
  try {
    console.log(`Getting encounter history for ${encounterId}`);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('limit', limitCount.toString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/encounters/${encounterId}/history?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.history;
    
  } catch (error) {
    console.error('Error fetching encounter history:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

/**
 * Get encounter history for a patient (across all encounters) using secure backend API
 */
export const getPatientEncounterHistory = async (patientId, limitCount = 100, facilityId = null) => {
  try {
    console.log(`Getting patient encounter history for ${patientId}`);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('limit', limitCount.toString());
    if (facilityId) {
      params.append('facilityId', facilityId);
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/patients/${patientId}/encounter-history?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.encounterHistory || response.data.history || [];
    
  } catch (error) {
    console.error('Error fetching patient encounter history:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

/**
 * Get encounter history with comprehensive filtering
 */
export const getEncounterHistoryWithFilters = async (filters = {}, page = 1, limit = 50) => {
  try {
    console.log('Getting encounter history with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/clinical/encounter-history?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      history: response.data.history,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error getting encounter history with filters:', error);
    throw error;
  }
};

/**
 * Get encounter history by change type
 */
export const getEncounterHistoryByChangeType = async (encounterId, changeType, limitCount = 50) => {
  try {
    const history = await getEncounterHistory(encounterId, limitCount * 2); // Get more to filter
    return history.filter(record => record.changeType === changeType).slice(0, limitCount);
  } catch (error) {
    console.error('Error getting encounter history by change type:', error);
    throw error;
  }
};

/**
 * Get recent encounter changes across all encounters for a facility
 */
export const getRecentEncounterChanges = async (facilityId, limitCount = 100) => {
  try {
    console.log(`Getting recent encounter changes for facility ${facilityId}`);
    
    const filters = {
      facilityId,
      // We can add more filters here if the backend supports them
    };
    
    const result = await getEncounterHistoryWithFilters(filters, 1, limitCount);
    return result.history;
  } catch (error) {
    console.error('Error getting recent encounter changes:', error);
    throw error;
  }
};

/**
 * Get encounter history statistics
 */
export const getEncounterHistoryStats = async (encounterId) => {
  try {
    console.log(`Getting encounter history statistics for ${encounterId}`);
    const history = await getEncounterHistory(encounterId, 1000); // Get all history
    
    // Calculate statistics
    const stats = {
      totalVersions: history.length,
      changeTypes: {},
      contributors: {},
      lastModified: null,
      clinicalNotesVersions: 0
    };
    
    history.forEach(record => {
      // Count change types
      stats.changeTypes[record.changeType] = (stats.changeTypes[record.changeType] || 0) + 1;
      
      // Count contributors
      const contributor = record.changedBy || 'Unknown';
      stats.contributors[contributor] = (stats.contributors[contributor] || 0) + 1;
      
      // Track last modified
      if (!stats.lastModified || new Date(record.changedAt) > new Date(stats.lastModified)) {
        stats.lastModified = record.changedAt;
      }
      
      // Count clinical notes versions
      if (record.clinicalData?.notes && hasClinicalChanges(record.clinicalData)) {
        stats.clinicalNotesVersions++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting encounter history statistics:', error);
    throw error;
  }
};

/**
 * Compare two encounter history versions
 */
export const compareEncounterVersions = async (encounterId, version1, version2) => {
  try {
    console.log(`Comparing encounter versions ${version1} and ${version2} for ${encounterId}`);
    const history = await getEncounterHistory(encounterId, 1000);
    
    const v1Record = history.find(record => record.version === version1);
    const v2Record = history.find(record => record.version === version2);
    
    if (!v1Record || !v2Record) {
      throw new Error('One or both versions not found');
    }
    
    // Simple comparison object - in a real implementation, you might want
    // to use a more sophisticated diff algorithm
    const comparison = {
      version1: {
        version: v1Record.version,
        changedAt: v1Record.changedAt,
        changedBy: v1Record.changedBy,
        changeType: v1Record.changeType
      },
      version2: {
        version: v2Record.version,
        changedAt: v2Record.changedAt,
        changedBy: v2Record.changedBy,
        changeType: v2Record.changeType
      },
      differences: {
        notes: compareNotes(v1Record.clinicalData?.notes, v2Record.clinicalData?.notes),
        clinicalFields: compareClinicalFields(v1Record.clinicalData, v2Record.clinicalData)
      }
    };
    
    return comparison;
  } catch (error) {
    console.error('Error comparing encounter versions:', error);
    throw error;
  }
};

/**
 * Helper function to compare notes between versions
 */
const compareNotes = (notes1 = {}, notes2 = {}) => {
  const allNoteTypes = new Set([...Object.keys(notes1), ...Object.keys(notes2)]);
  const differences = {};
  
  allNoteTypes.forEach(noteType => {
    const value1 = notes1[noteType] || '';
    const value2 = notes2[noteType] || '';
    
    if (value1 !== value2) {
      differences[noteType] = {
        version1: value1,
        version2: value2,
        changed: true
      };
    }
  });
  
  return differences;
};

/**
 * Helper function to compare clinical fields between versions
 */
const compareClinicalFields = (clinical1 = {}, clinical2 = {}) => {
  const fieldsToCompare = ['chiefComplaint', 'initialAssessment', 'actionPlan'];
  const differences = {};
  
  fieldsToCompare.forEach(field => {
    const value1 = clinical1[field] || '';
    const value2 = clinical2[field] || '';
    
    if (value1 !== value2) {
      differences[field] = {
        version1: value1,
        version2: value2,
        changed: true
      };
    }
  });
  
  return differences;
};

/**
 * Get encounter history summary for dashboard/reporting
 */
export const getEncounterHistorySummary = async (facilityId, dateRange = {}) => {
  try {
    console.log(`Getting encounter history summary for facility ${facilityId}`);
    
    const filters = {
      facilityId,
      ...dateRange
    };
    
    const result = await getEncounterHistoryWithFilters(filters, 1, 1000); // Get larger set for analysis
    const history = result.history;
    
    const summary = {
      totalChanges: history.length,
      changesByType: {},
      changesByUser: {},
      changesByDay: {},
      clinicalNotesChanges: 0,
      encountersModified: new Set()
    };
    
    history.forEach(record => {
      // Count by change type
      summary.changesByType[record.changeType] = (summary.changesByType[record.changeType] || 0) + 1;
      
      // Count by user
      const user = record.changedBy || 'Unknown';
      summary.changesByUser[user] = (summary.changesByUser[user] || 0) + 1;
      
      // Count by day
      const day = new Date(record.changedAt).toISOString().split('T')[0];
      summary.changesByDay[day] = (summary.changesByDay[day] || 0) + 1;
      
      // Count clinical notes changes
      if (record.clinicalData?.notes && hasClinicalChanges(record.clinicalData)) {
        summary.clinicalNotesChanges++;
      }
      
      // Track unique encounters
      summary.encountersModified.add(record.encounterId);
    });
    
    summary.encountersModified = summary.encountersModified.size;
    
    return summary;
  } catch (error) {
    console.error('Error getting encounter history summary:', error);
    throw error;
  }
};

/**
 * Export encounter history to a structured format
 */
export const exportEncounterHistory = async (encounterId, format = 'json') => {
  try {
    console.log(`Exporting encounter history for ${encounterId} in ${format} format`);
    const history = await getEncounterHistory(encounterId, 10000); // Get all history
    
    if (format === 'json') {
      return JSON.stringify(history, null, 2);
    } else if (format === 'csv') {
      // Simple CSV conversion - in production, you might want a more robust CSV library
      const headers = ['Version', 'Change Type', 'Changed By', 'Changed At', 'Notes Summary'];
      const rows = history.map(record => [
        record.version,
        record.changeType,
        record.changedBy || 'Unknown',
        record.changedAt,
        Object.values(record.clinicalData?.notes || {}).join(' ').substring(0, 100) + '...'
      ]);
      
      return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    }
    
    throw new Error(`Unsupported export format: ${format}`);
  } catch (error) {
    console.error('Error exporting encounter history:', error);
    throw error;
  }
};
